import i18next from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { DxButton, DxContainer, DxText } from '@dvag/design-system-react';

import { navigateToMicroApp } from '@dvag/dfs-orchestrator-client/navigation';
import { navigateNext, navigatePrevious } from '@dvag/dfs-orchestrator-client/messengers';
import { appNames } from '@dvag/dfs-constant-config/app-names';
import { sharedAppRoutes } from '@dvag/dfs-constant-config/shared-app-names';
import { useParams } from 'react-router-dom';
import { UnsavedOrPendingModal } from 'component/UnsavedOrPendingModal';
import { buildPath } from 'service/buildPath';
import { route } from 'config/route';
import { usePersonList } from 'hooks/usePersonList';
import { handleNavigateToPath } from 'service/handleNavigateToPath';
import { dashboardUtil } from 'service/model/dashboardUtil';
import { useNotification } from 'utils/notification/NotificationContext';
import { useContinuation } from 'utils/useContinuation';
import { WishGoalCardsMap } from './WishGoalCardsMap';
import { Footer } from './component/Footer';
import { Loading } from './component/Loading/Loading';
import { PersonDropdown } from './component/PersonDropdown/PersonDropdown';
import { CopyModal } from './modal/CopyModal/CopyModal';
import { useCopyWishGoalList } from './service/useCopyWishGoalList';
import { useGetPersonWishGoalList } from './service/useGetPersonWishGoalList';
import { getMonitoring } from '../../service/getMonitoring';

import './style.css';

const translation = {
  pageTitle: i18next.t('wishesAndGoals.headerTitle'),
  copyButtonText: i18next.t('wishesAndGoals.copyModal.copyButton'),
  showTopText: i18next.t('wishesAndGoals.showTop'),
  hideTopText: i18next.t('wishesAndGoals.hideTop'),
  toastErrorMessage: i18next.t('general.toasters.loading.generic.title'),
};

interface WishesAndGoalsProps {
  hideFinancialGrade?: boolean;
}

const updatePersonIdParam = (
  personId: string,
  hideFinancialGrade: boolean,
  householdId: string,
) => {
  const navigationRoute = hideFinancialGrade
    ? route.wishesAndGoalsHideFinancialGradeWithSelectedPerson
    : route.wishesAndGoalsWithSelectedPerson;
  navigateToMicroApp(
    'person',
    buildPath(navigationRoute, {
      householdId,
      personId,
    }),
  );
};

export const WishAndGoal = ({ hideFinancialGrade = false }: WishesAndGoalsProps) => {
  const { showError } = useNotification();
  const { householdId, personId: paramPersonId } = useParams();
  const personList = usePersonList();
  const mainPerson = personList.data?.[0] || null;
  const mainPersonId = mainPerson?.id ?? '';

  const [copyModalVisibility, setCopyModalVisibility] = useState(false);
  const selectedPersonId = paramPersonId ?? mainPersonId;
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: initialWgList,
    isLoading: isInitialWgListLoading,
    isFetching: isInitialWgListFetching,
  } = useGetPersonWishGoalList(selectedPersonId);

  const [wgList, setWgList] = useState(initialWgList);
  const copyWgList = useCopyWishGoalList();
  const { continuation, getContinuationHandler, getCanContinueHandler, onContinuationRequest } =
    useContinuation();
  const onCanContinueChange = useMemo(() => getCanContinueHandler(), [getCanContinueHandler]);

  const hasAtLeastOneWgSaved = wgList.some((wg) => wg.id);

  const closeCopyModal = () => setCopyModalVisibility(false);

  const openCopyModal = () => setCopyModalVisibility(true);

  useEffect(() => {
    if (initialWgList.length) {
      setWgList(initialWgList);
    }
  }, [initialWgList]);

  useEffect(() => {
    onCanContinueChange(!isDirty && !copyWgList.isPending);
  }, [onCanContinueChange, isDirty, copyWgList.isPending]);

  const monitoring = getMonitoring();

  const doCopy = async (selectedPersonIdList: string[]) => {
    if (!selectedPersonId) {
      showError({ title: translation.toastErrorMessage });
      return;
    }
    try {
      await copyWgList.mutateAsync({
        personId: selectedPersonId,
        personIdList: selectedPersonIdList,
      });
    } catch (e) {
      monitoring.trackException(e);
      showError({ title: translation.toastErrorMessage });
    }
  };

  const renderCopyButton = () => {
    const areAtLeastTwoPersons = personList.data?.length > 1;
    const isListNotEmpty = wgList.length > 0;
    if (isListNotEmpty && areAtLeastTwoPersons) {
      return (
        <DxButton
          id="wg-copy-button"
          type="secondary-m"
          data-testid="copy-button"
          onClick={openCopyModal}
          label={translation.copyButtonText}
          stretch
        />
      );
    }

    return null;
  };

  return (
    <DxContainer color="transparent" data-testid="wg-page-container">
      <DxContainer color="transparent" className="main-item-container">
        <DxContainer
          className="wishes-and-goals-title-container container-wrapper"
          color="transparent"
        >
          <DxText className="title" type="h3">
            {translation.pageTitle}
          </DxText>

          <PersonDropdown
            className="person-dropdown"
            selectedId={selectedPersonId}
            onChange={(newPersonId) => {
              getContinuationHandler(() => {
                updatePersonIdParam(newPersonId, hideFinancialGrade, householdId as string);
              })?.();
            }}
          />
        </DxContainer>
      </DxContainer>
      <DxContainer color="transparent" className="main-item-container">
        <DxContainer color="transparent" className="container-wrapper">
          <DxContainer color="transparent" className="wishes-and-goals-action-container">
            <DxContainer color="transparent" className="copy-button-container">
              {renderCopyButton()}
            </DxContainer>
          </DxContainer>
          <DxContainer color="transparent" className={`list-wrapper`}>
            {!selectedPersonId ||
            !wgList.length ||
            isInitialWgListLoading ||
            isInitialWgListFetching ? (
              <Loading props="show" />
            ) : (
              <WishGoalCardsMap
                personId={selectedPersonId}
                initialWgList={initialWgList}
                topDisplayType="show"
                setIsDirty={setIsDirty}
                setIsSubmitting={setIsSubmitting}
                onWgListChange={setWgList}
                onContinuationRequest={onContinuationRequest}
              />
            )}
          </DxContainer>
          <Footer
            handleBack={getContinuationHandler(() => {
              navigatePrevious(
                buildPath(`/${appNames.advisor}${sharedAppRoutes.advisor.householdDashboard}`, {
                  householdId: householdId as string,
                }),
              );
            })}
            handleContinue={getContinuationHandler(() => {
              navigateNext(
                buildPath(`/${appNames.vpd}${sharedAppRoutes.vpd.analysis.ffg.start}`, {
                  householdId: householdId as string,
                }),
              );
            })}
            handleGoToFinancialNote={getContinuationHandler(() => {
              handleNavigateToPath(
                sharedAppRoutes.person.financeNote,
                householdId,
                dashboardUtil.ACCOUNTING,
              );
            })}
            hideFinancialGrade={hideFinancialGrade}
          />
        </DxContainer>
      </DxContainer>
      <CopyModal
        visible={copyModalVisibility}
        isSubmitDisabled={isDirty || !hasAtLeastOneWgSaved}
        onClosed={closeCopyModal}
        personList={personList.data}
        personId={selectedPersonId}
        onCancel={closeCopyModal}
        onSubmit={(selectedPersonIdList) => {
          closeCopyModal();
          doCopy(selectedPersonIdList);
        }}
      />
      <UnsavedOrPendingModal
        showPending={continuation !== undefined && isSubmitting}
        showUnsaved={continuation !== undefined && !isSubmitting && isDirty}
        onCancel={continuation?.onCancel}
        onConfirm={continuation?.onConfirm}
      />
    </DxContainer>
  );
};
